import React, { useState, useEffect } from 'react'
import { useMediaQuery } from '@ui/stickybid'
import useRouter from 'use-react-router'

import ErrorMessage from '../../components/ErrorMessage'
import { Card, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core'
import ClientSearchTable from '../../components/ClientSearchTable'
import NewClientButton from '../../components/NewClientButton'
import { useSnackbar } from 'notistack'

import { NavHeader } from '../../components/NavDrawer'

import { jsonToLucene } from 'json-to-lucene'
import type { ValueFilter } from 'json-to-lucene'
import { useLazyQuery } from '@apollo/react-hooks'

import type { SearchClientsResponse } from '../../graphql/queries/SEARCH_CLIENTS'
import { SEARCH_CLIENTS } from '../../graphql/queries/SEARCH_CLIENTS'
import ClientSearchBar from '../../components/ClientSearchBar/ClientSearchBar'
import { getFiltersFromLocation } from '../../components/ClientSearchBar/ClientSearchBar'
import { setUrlQuery } from '../../util/setUrlQuery'
import { getUrlQuery } from 'src/util'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {},
  searchBar: {
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
      gridColumn: '1 / 3'
    }
  },
  searchContainer: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start'
    },
    '& input': {
      minWidth: 400,
      [theme.breakpoints.down('md')]: {
        minWidth: 260
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: 'unset'
      }
    }
  },
  filterChips: {
    '&$filterChips': {
      gridRow: 3,
      gridColumn: '1 / -1',
      justifyContent: 'flex-start',
      marginLeft: theme.spacing(-1)
    }
  }
}))

export interface SearchProps {
  text: string
  filters: ValueFilter[]
  isInitialSearch?: boolean
}

export interface UrlParams {
  sort: string
  page: number
  search: string
}

export default function SearchClients() {
  const classes = useStyles({})
  const { location, history } = useRouter<any, any, { page: number }>()
  const urlParams = getUrlQuery<UrlParams>({ location })
  const sort = urlParams.sort || '-updated_date'
  const { enqueueSnackbar } = useSnackbar()
  const smDown = useMediaQuery('sm')
  const limit = 50
  const [page, setPage] = useState(location.state?.page ?? 1)
  const [filters, setFilters] = useState(getFiltersFromLocation(location))
  const [query, setQuery] = useState('') // will get populated from ContactSearchBar

  const [search, { loading, data, error, fetchMore }] = useLazyQuery<SearchClientsResponse>(SEARCH_CLIENTS, {
    variables: {
      query,
      limit
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (newData: SearchClientsResponse) => {
      // reset page back to 1 if user refreshes the page (cache is reset)
      if (
        page * limit > newData?.searchClients?.rows.length &&
        newData?.searchClients?.rows.length !== newData?.searchClients?.total_rows
      ) {
        setPage(1)
      }
    }
  })

  function handleClientSearch({ text, filters, isInitialSearch }: SearchProps) {
    let query = jsonToLucene({ query: text ?? '', filters }) || '*:*'
    if (query === '') {
      query = '*:*'
    }

    setUrlQuery({
      location,
      params: { search: text || null, filters },
      replaceParams: true
    })

    setQuery(query)
    setFilters(filters)

    if (!isInitialSearch) {
      setPage(1)
    }

    search({
      variables: {
        query,
        sort: [sort, '-updated_date'],
        limit
      }
    })
  }

  useEffect(() => {
    history.replace({
      ...history.location,
      state: { page }
    })
  }, [page, history])

  // show error toast
  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Unable to search Clients`, { variant: 'error' })
    }
  }, [error, enqueueSnackbar])

  const handleRequestSort = (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`

    setPage(1)
    setUrlQuery({ location, params: { sort: newSort } })

    search({
      variables: {
        query: query ?? '*:*',
        sort: [newSort, '-updated_date']
      }
    })
  }

  const { total_rows = 0, rows = [] } = data?.searchClients ?? {}

  return (
    <div className={classes.root}>
      <NavHeader title="Clients" />
      <NewClientButton />
      <ClientSearchBar
        classes={{
          clientSearchRoot: classes.searchBar,
          container: classes.searchContainer,
          filterChips: classes.filterChips
        }}
        fullWidth={smDown ? true : false}
        loading={loading}
        filters={filters}
        showChips={true}
        onSearch={(query, filters, isInitialSearch) => {
          handleClientSearch({
            text: query,
            filters,
            isInitialSearch
          })
        }}
      />
      {error && <ErrorMessage>{error.message}</ErrorMessage>}

      <Card>
        <ClientSearchTable
          clients={loading ? [] : rows}
          loading={loading}
          rowsPerPage={limit}
          sort={sort}
          page={page}
          pages={Math.ceil(total_rows / limit)}
          onPageChange={(newPage) => setPage(newPage)}
          onRequestSort={handleRequestSort}
          onFetchNextPage={() => {
            fetchMore({
              variables: {
                query: query ?? '*:*',
                limit,
                bookmark: data.searchClients.bookmark
              },
              updateQuery(prev, { fetchMoreResult }) {
                if (!fetchMoreResult) {
                  return prev
                }

                return {
                  ...prev,
                  searchClients: {
                    ...prev.searchClients,
                    bookmark: fetchMoreResult.searchClients.bookmark,
                    rows: [...prev.searchClients.rows, ...fetchMoreResult.searchClients.rows]
                  }
                }
              }
            })
          }}
        />
      </Card>
    </div>
  )
}
